/**
 * premium-box-upsell.js
 * ---------------------------------------------------------------
 * ONE-FILE Premium Gift Box Upsell — Shopify Horizon, Non-Plus.
 * Store: wardacreationss.com
 *
 * AUDIT NOTES — what changed from the version you sent, and why:
 *
 *   1. merchandiseId format bug (real bug, would silently misbehave):
 *      `Shopify.actions.updateCart` writes through the Storefront API,
 *      and the Storefront API's `merchandiseId` field requires a full
 *      GID (`gid://shopify/ProductVariant/<id>`), not a bare numeric
 *      id. The previous file passed the bare numeric variant id
 *      straight through. On any theme build that has adopted
 *      Shopify's Standard Storefront Actions runtime, that call would
 *      either fail or silently no-op. Fixed: we now build the GID
 *      explicitly for the actions.updateCart path, and keep the bare
 *      numeric id for the REST `/cart/add.js` fallback (REST wants
 *      the plain numeric id, confirmed against Shopify's Ajax Cart
 *      API reference).
 *
 *   2. Buy It Now was completely unhandled in the file you sent —
 *      there was no code for it at all. This is the hard part of the
 *      brief, so it gets its own section (12–16 below). Short version
 *      of the approach, full reasoning is in the chat response:
 *        - Shopify's accelerated checkout buttons (Shop Pay / Apple
 *          Pay / Google Pay / PayPal / plain "Buy it now") render
 *          through a Shopify-controlled web component that, for
 *          branded methods, draws its UI inside a cross-origin
 *          iframe. That iframe cannot be reached or click-intercepted
 *          by theme JS — this is intentional on Shopify's part (PCI
 *          scope, tamper-resistance), not a bug to work around.
 *        - So instead of trying to intercept the button's own click,
 *          we place a fully same-origin, same-page transparent
 *          "shield" element on top of the button's container. Native
 *          button, its shadow DOM, and its iframe are never touched,
 *          moved, or reparented — the shield is a sibling, positioned
 *          with JS-measured coordinates and kept in sync with a
 *          ResizeObserver.
 *        - Clicking the shield opens our own upgrade modal. Whichever
 *          choice the customer makes, WE add the correct line item(s)
 *          via the real `/cart/add.js` endpoint (never a fake price),
 *          then send the browser to `/checkout` ourselves. We never
 *          try to reach into or drive Shopify's native button/iframe.
 *        - Deliberate, disclosed behavior change: this makes Buy It
 *          Now check out the customer's FULL current cart (existing
 *          items + the just-selected product, i.e. exactly what
 *          "Add to Cart" + "Checkout" would do), instead of Shopify's
 *          native behavior of checking out ONLY the just-selected
 *          product and ignoring whatever is already in the cart. This
 *          is required to satisfy your requirement (C) — it is the
 *          only reliable, Shopify-supported way to guarantee an
 *          already-added Premium Gift Box is never dropped by Buy It
 *          Now. Full explanation in the chat response.
 *        - Known, honestly-disclosed limitation: a keyboard-only
 *          customer who Tabs directly onto the native button and
 *          activates it with Enter/Space (without a pointer click)
 *          bypasses the shield, because keyboard activation fires on
 *          the focused native element directly, not through our
 *          overlay. There is no unsupported-API-free way to close
 *          this gap on a non-Plus store. See chat response.
 *
 *   3. Mount tracking (kept from previous file, this was already
 *      correct): live DOM-reference checks instead of static
 *      "mounted" flags, so a banner wiped out by a Horizon re-render
 *      gets recreated instead of vanishing forever.
 *
 *   4. Everything else — banner, modal, cart-drawer/cart-page/PDP
 *      mounting, MutationObserver-driven resync, duplicate-add
 *      protection — is your working implementation, kept as-is.
 * ---------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.__premiumBoxUpsellInitialized) return;
  window.__premiumBoxUpsellInitialized = true;

  /* ================================================================
     1. CONFIGURATION — already filled with your real store values
     ================================================================ */
  const PREMIUM_BOX_CONFIG = {
    variantId: '54032100163695',
    imageUrl: 'https://wardacreationss.com/cdn/shop/files/4982f542-ff5a-4094-acdd-f5aa747d4a3d.webp?v=1786654805&width=1200',
    title: 'Premium Gift Box',
    priceText: 'Rs. 1,500', // DISPLAY ONLY — Shopify's real variant price is what is charged
    description: 'Make your order extra special with our premium packaging.',
    offerTitle: 'Upgrade to Premium Packaging',
    addCtaLabel: 'Add Premium Gift Box',
    selectCtaLabel: 'Add Premium Gift Box — Rs. 1,500',
    noThanksLabel: 'No Thanks',
    closeLabel: 'Close',
    successLabel: '✓ Premium Gift Box Added',
    addingLabel: 'Adding Premium Gift Box…',
    alreadyAddedLabel: '✓ Premium Gift Box Added',
    errorLabel: 'Unable to add Premium Gift Box. Please try again.',
    autoCloseDelay: 5000,

    // Buy It Now upsell copy
    buyNowTitle: 'Complete Your Order',
    buyNowDesc: "You're about to check out — would you like to add premium packaging first?",
    buyNowSelectLabel: 'Add Premium Gift Box — Rs. 1,500',
    buyNowDeclineLabel: 'Continue without Premium Box',
    buyNowProcessingLabel: 'Adding to your order…',
    buyNowContinuingLabel: 'Continuing to checkout…',
    buyNowErrorLabel: "Something went wrong. Please try again, or use your cart to check out.",
    enableBuyNowIntercept: true,

    debug: false, // set true only while testing — extra console logs
  };

  const LOCK_WINDOW_MS = 400;
  const SESSION_DISMISS_KEY = 'pbu:dismissedThisSession';

  // Confirmed from cart-drawer.js you sent.
  const DRAWER_SELECTORS = ['cart-drawer-component', '#CartDrawer', '[data-cart-drawer]', '.cart-drawer'];

  // Confirmed from cart-products.liquid you sent.
  const CART_PAGE_SELECTORS = ['.cart-items__wrapper', '#cart-form', '.cart-form', '.cart__contents'];

  // Not confirmed from source — conservative fallback chain.
  const PRODUCT_FORM_SELECTORS = [
    'product-form-component',
    'form[action*="/cart/add"]',
    '[data-type="add-to-cart-form"]',
    '.product-form',
  ];

  // NOT confirmed against your live theme markup (I don't have DOM
  // access to your storefront's rendered HTML, only its text/content).
  // This is a best-effort, ordered fallback chain covering every
  // known Shopify accelerated-checkout container pattern. VERIFY on
  // your live product page: right-click the "Buy it now" button →
  // Inspect → confirm one of these matches, or add the real one as
  // the first entry. See the installation notes in the chat response.
  const BUY_NOW_CONTAINER_SELECTORS = [
    'shopify-accelerated-checkout',
    'shopify-accelerated-checkout-cart',
    '.shopify-payment-button',
    '[data-shopify="payment-button"]',
    '.product-form__payment-container',
    '.product-form__buttons .shopify-payment-button',
  ];

  let themeEventsModule = null; // cached dynamic import of '@theme/events'

  function log(...args) {
    if (PREMIUM_BOX_CONFIG.debug) console.log('[PremiumBoxUpsell]', ...args);
  }

  /* ================================================================
     2. SELF-INJECTED, SCOPED CSS
     ================================================================ */
  (function injectStyles() {
    if (document.querySelector('style[data-premium-box-upsell]')) return; // idempotent even if script double-loads
    const style = document.createElement('style');
    style.setAttribute('data-premium-box-upsell', '');
    style.textContent = `
      .premium-box-upsell{--pbu-radius:14px;margin:16px 0;padding:14px 16px;border:1px solid rgba(0,0,0,.1);border-radius:var(--pbu-radius);background:linear-gradient(180deg,#fbfaf8 0%,#f5f2ee 100%);font-family:inherit;}
      .premium-box-upsell[hidden]{display:none !important;}
      .premium-box-upsell__button{display:flex;align-items:center;gap:12px;width:100%;border:none;background:transparent;padding:0;text-align:left;cursor:pointer;font-family:inherit;min-height:44px;}
      .premium-box-upsell__button:focus-visible{outline:2px solid currentColor;outline-offset:4px;border-radius:8px;}
      .premium-box-upsell__icon{font-size:22px;flex:0 0 auto;}
      .premium-box-upsell__text{display:flex;flex-direction:column;flex:1 1 auto;min-width:0;}
      .premium-box-upsell__title{font-weight:600;font-size:14px;}
      .premium-box-upsell__subtitle{font-size:12px;opacity:.7;margin-top:2px;}
      .premium-box-upsell__cta{flex:0 0 auto;font-size:13px;font-weight:600;padding:8px 14px;border-radius:999px;background:#111;color:#fff;white-space:nowrap;}
      .premium-box-upsell__success{margin:0;font-size:13px;font-weight:600;color:#1a7f37;}
      .premium-box-upsell__error{margin:8px 0 0;font-size:12px;color:#c0392b;}

      .pbu-modal{position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;font-family:inherit;}
      .pbu-modal[hidden]{display:none;}
      .pbu-modal__backdrop{position:absolute;inset:0;background:rgba(20,18,16,.55);backdrop-filter:blur(2px);}
      .pbu-modal__panel{position:relative;width:min(420px,calc(100vw - 32px));max-height:calc(100vh - 32px);overflow-y:auto;background:#fff;border-radius:20px;box-shadow:0 24px 60px rgba(0,0,0,.25);padding:28px 24px 24px;display:flex;flex-direction:column;align-items:center;text-align:center;}
      .pbu-modal__close{position:absolute;top:12px;right:12px;width:36px;height:36px;border-radius:999px;border:none;background:rgba(0,0,0,.05);font-size:18px;line-height:1;cursor:pointer;}
      .pbu-modal__close:focus-visible{outline:2px solid currentColor;outline-offset:2px;}
      .pbu-modal__image-wrap{width:100%;max-width:260px;aspect-ratio:1/1;border-radius:14px;overflow:hidden;background:#f5f2ee;margin-bottom:18px;}
      .pbu-modal__image{width:100%;height:100%;object-fit:contain;display:block;}
      .pbu-modal__title{margin:0 0 6px;font-size:19px;font-weight:700;}
      .pbu-modal__desc{margin:0 0 10px;font-size:14px;opacity:.75;line-height:1.5;}
      .pbu-modal__price{margin:0 0 18px;font-size:17px;font-weight:700;}
      .pbu-modal__status{margin:0 0 14px;font-size:13px;font-weight:600;color:#1a7f37;}
      .pbu-modal__status.is-error{color:#c0392b;}
      .pbu-modal__actions{display:flex;flex-direction:column;gap:10px;width:100%;}
      .pbu-modal__select{width:100%;min-height:48px;border:none;border-radius:999px;padding:14px 18px;font-size:15px;font-weight:700;background:#111;color:#fff;cursor:pointer;}
      .pbu-modal__select:disabled{opacity:.6;cursor:default;}
      .pbu-modal__select:focus-visible,.pbu-modal__decline:focus-visible{outline:2px solid currentColor;outline-offset:3px;}
      .pbu-modal__decline{width:100%;min-height:44px;border:none;background:transparent;padding:8px;font-size:13px;opacity:.6;cursor:pointer;}
      .pbu-modal__decline:disabled{opacity:.35;cursor:default;}

      @media (max-width:480px){
        .pbu-modal{align-items:flex-end;}
        .pbu-modal__panel{width:100%;max-width:none;border-radius:20px 20px 0 0;padding:24px 20px calc(20px + env(safe-area-inset-bottom));}
      }
      @media (min-width:480px){ .pbu-modal__panel{padding:32px 32px 28px;} }

      .pbu-buynow-shield{position:absolute;z-index:6;background:transparent;cursor:pointer;-webkit-tap-highlight-color:transparent;}
      .pbu-buynow-shield:focus-visible{outline:2px solid #111;outline-offset:2px;}
      .pbu-buynow-shield[data-pbu-busy="1"]{cursor:progress;}
    `;
    document.head.appendChild(style);
  })();

  /* ================================================================
     3. ROUTES (locale-aware, never assumes window.Theme exists)
     ================================================================ */
  function getThemeRoutes() {
    try {
      // `typeof` never throws on an undeclared identifier — safe probe
      // for a top-level `const Theme` that is NOT a window property.
      if (typeof Theme !== 'undefined' && Theme && Theme.routes) return Theme.routes;
    } catch (e) {
      /* ignore */
    }
    return null;
  }

  function getRoutes() {
    const themeRoutes = getThemeRoutes();
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const cartAddUrl = (themeRoutes && themeRoutes.cart_add_url) || root + 'cart/add.js';
    const cartUrl = (themeRoutes && themeRoutes.cart_url) || root + 'cart';
    const cartJsonUrl = cartUrl.replace(/\.js$/, '').replace(/\/$/, '') + '.js';
    const checkoutUrl = root.replace(/\/$/, '') + '/checkout';
    return { cartAddUrl, cartJsonUrl, root, checkoutUrl };
  }

  /* ================================================================
     4. THEME EVENTS INTEGRATION (@theme/events, confirmed importmap)
     ================================================================ */
  function loadThemeEvents() {
    if (themeEventsModule) return themeEventsModule;
    themeEventsModule = import('@theme/events').catch((err) => {
      log('Could not load @theme/events; falling back to generic events.', err);
      return null;
    });
    return themeEventsModule;
  }

  async function getCartAddEventName() {
    const mod = await loadThemeEvents();
    return (mod && mod.CartAddEvent && mod.CartAddEvent.eventName) || 'cart:add';
  }

  /** Nudges cart-drawer-component (and anything else listening) the
   *  same way Horizon's own product form does after a real add. */
  async function notifyThemeOfCartChange(cart) {
    const eventName = await getCartAddEventName();
    document.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        detail: { resource: { item_count: cart ? cart.item_count : undefined } },
      })
    );
    document.dispatchEvent(new CustomEvent('premium_box_upsell:cart-changed', { bubbles: true, detail: { cart } }));
  }

  /* ================================================================
     5. CART DATA HELPERS — /cart.js is the source of truth
     ================================================================ */
  async function fetchCart() {
    const { cartJsonUrl } = getRoutes();
    const res = await fetch(cartJsonUrl, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Failed to read cart (' + res.status + ')');
    return res.json();
  }

  function findPremiumBoxLine(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    return cart.items.find((item) => String(item.variant_id) === String(PREMIUM_BOX_CONFIG.variantId)) || null;
  }

  function hasOtherItems(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some((item) => String(item.variant_id) !== String(PREMIUM_BOX_CONFIG.variantId));
  }

  /* ================================================================
     6. ADD TO CART (real variant, real price, duplicate-safe)
     ================================================================ */
  function hasStandardActions() {
    return !!(window.Shopify && window.Shopify.actions && typeof window.Shopify.actions.updateCart === 'function');
  }

  function toVariantGid(variantId) {
    return 'gid://shopify/ProductVariant/' + String(variantId);
  }

  async function addPremiumBoxToCart() {
    // Authoritative re-check immediately before writing.
    const current = await fetchCart();
    if (findPremiumBoxLine(current)) return current;

    if (hasStandardActions()) {
      try {
        const { userErrors } = await window.Shopify.actions.updateCart(
          { lines: [{ merchandiseId: toVariantGid(PREMIUM_BOX_CONFIG.variantId), quantity: 1 }] },
          { event: { context: 'dialog', detail: { source: 'premium-box-upsell' } } }
        );
        if (userErrors && userErrors.length) throw new Error(userErrors[0].message || 'Cart update was rejected.');
        const finalCart = await fetchCart(); // re-read via /cart.js for our own variant_id-based state
        await notifyThemeOfCartChange(finalCart);
        return finalCart;
      } catch (err) {
        log('Shopify.actions.updateCart failed, falling back to /cart/add.js', err);
        // fall through to REST fallback below
      }
    }

    const { cartAddUrl } = getRoutes();
    const res = await fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: PREMIUM_BOX_CONFIG.variantId, quantity: 1 }] }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.description || body.message || 'Add to cart failed.');
    }
    const finalCart = await fetchCart();
    await notifyThemeOfCartChange(finalCart);
    return finalCart;
  }

  async function openCartUI() {
    if (hasStandardActions() && typeof window.Shopify.actions.openCart === 'function') {
      try {
        await window.Shopify.actions.openCart();
        return;
      } catch (e) {
        /* fall through to direct component call below */
      }
    }
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  /* ================================================================
     7. MODAL (singleton) — used for the normal "add to cart" upsell
     ================================================================ */
  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  const PremiumBoxModal = (function () {
    let dialogEl = null;
    let closeTimer = null;
    let lastFocused = null;
    let handlers = null;

    function build() {
      if (dialogEl) return dialogEl;
      dialogEl = document.createElement('div');
      dialogEl.className = 'pbu-modal';
      dialogEl.setAttribute('role', 'dialog');
      dialogEl.setAttribute('aria-modal', 'true');
      dialogEl.setAttribute('aria-labelledby', 'pbu-modal-title');
      dialogEl.hidden = true;
      dialogEl.innerHTML = `
        <div class="pbu-modal__backdrop" data-pbu-close></div>
        <div class="pbu-modal__panel">
          <button type="button" class="pbu-modal__close" data-pbu-close aria-label="${escapeHtml(PREMIUM_BOX_CONFIG.closeLabel)}"><span aria-hidden="true">&times;</span></button>
          <div class="pbu-modal__image-wrap"><img class="pbu-modal__image" src="${PREMIUM_BOX_CONFIG.imageUrl}" alt="${escapeHtml(PREMIUM_BOX_CONFIG.title)}" loading="eager" width="600" height="600"></div>
          <div class="pbu-modal__body">
            <h2 id="pbu-modal-title" class="pbu-modal__title">${escapeHtml(PREMIUM_BOX_CONFIG.title)}</h2>
            <p class="pbu-modal__desc">${escapeHtml(PREMIUM_BOX_CONFIG.description)}</p>
            <p class="pbu-modal__price">${escapeHtml(PREMIUM_BOX_CONFIG.priceText)}</p>
            <div class="pbu-modal__status" role="status" aria-live="polite" hidden></div>
            <div class="pbu-modal__actions">
              <button type="button" class="pbu-modal__select" data-pbu-select>${escapeHtml(PREMIUM_BOX_CONFIG.selectCtaLabel)}</button>
              <button type="button" class="pbu-modal__decline" data-pbu-decline>${escapeHtml(PREMIUM_BOX_CONFIG.noThanksLabel)}</button>
            </div>
          </div>
        </div>`;
      document.body.appendChild(dialogEl);

      dialogEl.addEventListener('click', (e) => {
        if (e.target.closest('[data-pbu-close]') || e.target.closest('[data-pbu-decline]')) {
          cancelTimer();
          if (handlers && handlers.onDismiss) handlers.onDismiss();
          close();
          return;
        }
        if (e.target.closest('[data-pbu-select]')) {
          cancelTimer();
          if (handlers && handlers.onSelect) handlers.onSelect();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (dialogEl.hidden) return;
        if (e.key === 'Escape') {
          cancelTimer();
          if (handlers && handlers.onDismiss) handlers.onDismiss();
          close();
        }
        if (e.key === 'Tab') trapFocus(e);
      });

      return dialogEl;
    }

    function trapFocus(e) {
      const focusable = dialogEl.querySelectorAll('button, [href], input, [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function cancelTimer() {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
    }

    function open(h) {
      handlers = h || {};
      const el = build();
      const status = el.querySelector('.pbu-modal__status');
      status.hidden = true;
      status.classList.remove('is-error');
      status.textContent = '';
      el.querySelector('.pbu-modal__actions').hidden = false;
      const selectBtnInit = el.querySelector('[data-pbu-select]');
      selectBtnInit.disabled = false;
      selectBtnInit.textContent = PREMIUM_BOX_CONFIG.selectCtaLabel;
      const declineBtnInit = el.querySelector('[data-pbu-decline]');
      declineBtnInit.disabled = false;
      declineBtnInit.hidden = false;
      declineBtnInit.textContent = PREMIUM_BOX_CONFIG.noThanksLabel;

      lastFocused = document.activeElement;
      el.hidden = false;
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        const selectBtn = el.querySelector('[data-pbu-select]');
        if (selectBtn) selectBtn.focus();
      });

      closeTimer = setTimeout(() => {
        if (handlers && handlers.onDismiss) handlers.onDismiss();
        close();
      }, PREMIUM_BOX_CONFIG.autoCloseDelay);

      const panel = el.querySelector('.pbu-modal__panel');
      const cancelOnFirstInteract = () => {
        cancelTimer();
        panel.removeEventListener('pointerdown', cancelOnFirstInteract);
        panel.removeEventListener('keydown', cancelOnFirstInteract);
      };
      panel.addEventListener('pointerdown', cancelOnFirstInteract, { once: true });
      panel.addEventListener('keydown', cancelOnFirstInteract, { once: true });
    }

    function close() {
      if (!dialogEl) return;
      cancelTimer();
      dialogEl.hidden = true;
      document.body.style.overflow = '';
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
      handlers = null;
    }

    function setAdding() {
      if (!dialogEl) return;
      const btn = dialogEl.querySelector('[data-pbu-select]');
      btn.disabled = true;
      btn.textContent = PREMIUM_BOX_CONFIG.addingLabel;
    }

    function showSuccess() {
      if (!dialogEl) return;
      const status = dialogEl.querySelector('.pbu-modal__status');
      status.hidden = false;
      status.classList.remove('is-error');
      status.textContent = PREMIUM_BOX_CONFIG.successLabel;
      dialogEl.querySelector('.pbu-modal__actions').hidden = true;
      setTimeout(close, 1100);
    }

    function showError(message) {
      if (!dialogEl) return;
      const btn = dialogEl.querySelector('[data-pbu-select]');
      btn.disabled = false;
      btn.textContent = PREMIUM_BOX_CONFIG.selectCtaLabel;
      const status = dialogEl.querySelector('.pbu-modal__status');
      status.hidden = false;
      status.classList.add('is-error');
      status.textContent = message;
    }

    return { open, close, setAdding, showSuccess, showError };
  })();

  /* ================================================================
     8. BANNER — one live instance per mount point, all kept in sync
     ================================================================ */
  const mountedBanners = new Set();

  function createBanner() {
    const wrap = document.createElement('div');
    wrap.className = 'premium-box-upsell';
    wrap.hidden = true;
    wrap.innerHTML = `
      <button type="button" class="premium-box-upsell__button" aria-haspopup="dialog">
        <span class="premium-box-upsell__icon" aria-hidden="true">🎁</span>
        <span class="premium-box-upsell__text">
          <span class="premium-box-upsell__title">${escapeHtml(PREMIUM_BOX_CONFIG.offerTitle)}</span>
          <span class="premium-box-upsell__subtitle">${escapeHtml(PREMIUM_BOX_CONFIG.description)}</span>
        </span>
        <span class="premium-box-upsell__cta">${escapeHtml(PREMIUM_BOX_CONFIG.addCtaLabel)}</span>
      </button>
      <p class="premium-box-upsell__success" role="status" aria-live="polite" hidden><span aria-hidden="true">✓</span> ${escapeHtml(
        PREMIUM_BOX_CONFIG.alreadyAddedLabel
      )}</p>
      <p class="premium-box-upsell__error" role="alert" hidden></p>
    `;

    let isAdding = false;
    let lastClickAt = 0;

    wrap.querySelector('.premium-box-upsell__button').addEventListener('click', () => {
      dispatchAnalytics('modal_opened');
      PremiumBoxModal.open({
        onSelect: () => handleAdd(),
        onDismiss: () => {
          sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
          dispatchAnalytics('dismissed');
          syncAllBanners();
        },
      });
    });

    async function handleAdd() {
      const now = Date.now();
      if (isAdding || now - lastClickAt < LOCK_WINDOW_MS) return;
      lastClickAt = now;
      isAdding = true;
      PremiumBoxModal.setAdding();

      const errorEl = wrap.querySelector('.premium-box-upsell__error');
      errorEl.hidden = true;

      try {
        const cart = await addPremiumBoxToCart();
        PremiumBoxModal.showSuccess();
        await openCartUI();
        dispatchAnalytics('added', { cart });
        syncAllBanners(cart);
      } catch (err) {
        console.error('[PremiumBoxUpsell] Add failed:', err);
        errorEl.textContent = PREMIUM_BOX_CONFIG.errorLabel;
        errorEl.hidden = false;
        PremiumBoxModal.showError(PREMIUM_BOX_CONFIG.errorLabel);
        dispatchAnalytics('add_failed', { message: err && err.message });
      } finally {
        isAdding = false;
      }
    }

    wrap.__pbuUpdate = function (cart) {
      const dismissed = sessionStorage.getItem(SESSION_DISMISS_KEY) === '1';
      const line = findPremiumBoxLine(cart);
      const btn = wrap.querySelector('.premium-box-upsell__button');
      const success = wrap.querySelector('.premium-box-upsell__success');

      if (line) {
        wrap.hidden = false;
        btn.hidden = true;
        success.hidden = false;
        return;
      }
      if (!hasOtherItems(cart) || dismissed) {
        wrap.hidden = true;
        return;
      }
      wrap.hidden = false;
      btn.hidden = false;
      success.hidden = true;
    };

    mountedBanners.add(wrap);
    return wrap;
  }

  function dispatchAnalytics(name, detail) {
    document.dispatchEvent(new CustomEvent('premium_box_' + name, { bubbles: true, detail: detail || {} }));
  }

  let syncQueued = false;
  async function syncAllBanners(preFetchedCart) {
    if (syncQueued) return;
    syncQueued = true;
    try {
      const cart = preFetchedCart || (await fetchCart().catch(() => null));
      if (!cart) return;
      // Opportunistically drop references to detached banners.
      mountedBanners.forEach((el) => {
        if (!document.body.contains(el)) {
          mountedBanners.delete(el);
          return;
        }
        if (typeof el.__pbuUpdate === 'function') el.__pbuUpdate(cart);
      });
    } finally {
      syncQueued = false;
    }
  }

  /* ================================================================
     9. MOUNTING — cart drawer, cart page, product page
        Uses LIVE DOM-reference checks (not static flags) so a
        banner that gets wiped out by a Horizon re-render is
        recreated on the next check instead of vanishing forever.
     ================================================================ */
  let drawerBannerRef = null;
  let cartPageBannerRef = null;
  let pdpBannerRef = null;

  function firstMatch(selectors, root) {
    const scope = root || document;
    for (const sel of selectors) {
      const el = scope.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  function mountInDrawer() {
    const drawer = firstMatch(DRAWER_SELECTORS);
    if (!drawer) return;
    if (drawerBannerRef && document.body.contains(drawerBannerRef)) return; // already live
    const target = drawer.querySelector('.cart-drawer__summary') || drawer;
    const banner = createBanner();
    if (target === drawer) {
      target.appendChild(banner);
    } else {
      target.parentElement.insertBefore(banner, target);
    }
    drawerBannerRef = banner;
    log('Mounted banner in cart drawer.');
  }

  function mountOnCartPage() {
    if (!/\/cart(\/|$)/.test(window.location.pathname)) return;
    const target = firstMatch(CART_PAGE_SELECTORS);
    if (!target) return;
    if (cartPageBannerRef && document.body.contains(cartPageBannerRef)) return;
    const banner = createBanner();
    target.insertAdjacentElement('afterend', banner);
    cartPageBannerRef = banner;
    log('Mounted banner on cart page.');
  }

  function mountOnProductPage() {
    const form = firstMatch(PRODUCT_FORM_SELECTORS);
    if (!form) return;
    if (pdpBannerRef && document.body.contains(pdpBannerRef)) return;
    const banner = createBanner();
    form.insertAdjacentElement('afterend', banner);
    pdpBannerRef = banner;
    log('Mounted banner on product page.');
  }

  function mountAll() {
    mountInDrawer();
    mountOnCartPage();
    mountOnProductPage();
    if (PREMIUM_BOX_CONFIG.enableBuyNowIntercept) {
      try {
        shieldBuyNowButtons();
      } catch (err) {
        log('Buy It Now shielding pass failed (native button left untouched):', err);
      }
    }
  }

  /* ================================================================
     10. LIVE UPDATES — real Horizon events + a single debounced
         MutationObserver (re-mount check AND state resync together,
         so remove/quantity-change/re-render all self-heal)
     ================================================================ */
  function wireLiveUpdates() {
    getCartAddEventName().then((eventName) => {
      document.addEventListener(eventName, () => {
        mountAll();
        syncAllBanners();
      });
    });

    document.addEventListener('premium_box_upsell:cart-changed', (e) => {
      mountAll();
      syncAllBanners(e.detail && e.detail.cart);
    });

    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        mountAll();
        syncAllBanners();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ================================================================
     11. FORM / VARIANT HELPERS — used by the Buy It Now flow to know
         which product + quantity the customer actually selected
     ================================================================ */
  function getSelectedVariantId(form) {
    if (!form) return null;
    const el = (form.elements && form.elements['id']) || form.querySelector('[name="id"]');
    const value = el && (el.value != null ? el.value : el.getAttribute && el.getAttribute('value'));
    return value ? String(value) : null;
  }

  function getSelectedQuantity(form) {
    if (!form) return 1;
    const el = (form.elements && form.elements['quantity']) || form.querySelector('[name="quantity"]');
    const q = el ? parseInt(el.value, 10) : 1;
    return !q || q < 1 ? 1 : q;
  }

  function findAssociatedForm(container) {
    if (!container) return null;
    const direct = container.closest && container.closest('form');
    if (direct) return direct;
    const scope =
      (container.closest &&
        container.closest('product-form-component, .shopify-section, [id^="MainProduct"], .product')) ||
      container.parentElement;
    return scope ? firstMatch(PRODUCT_FORM_SELECTORS, scope) : null;
  }

  /* ================================================================
     12. BUY IT NOW MODAL — separate singleton, distinct copy/behavior
         from the normal upsell modal. A decision is always required
         to move forward (Escape / backdrop / timeout = "continue
         without", never a silent do-nothing, because the customer
         already expressed checkout intent by clicking Buy It Now).
     ================================================================ */
  const BuyNowModal = (function () {
    let dialogEl = null;
    let closeTimer = null;
    let lastFocused = null;
    let handlers = null;

    function build() {
      if (dialogEl) return dialogEl;
      dialogEl = document.createElement('div');
      dialogEl.className = 'pbu-modal';
      dialogEl.setAttribute('role', 'dialog');
      dialogEl.setAttribute('aria-modal', 'true');
      dialogEl.setAttribute('aria-labelledby', 'pbu-buynow-title');
      dialogEl.hidden = true;
      dialogEl.innerHTML = `
        <div class="pbu-modal__backdrop" data-pbu-close></div>
        <div class="pbu-modal__panel">
          <button type="button" class="pbu-modal__close" data-pbu-close aria-label="${escapeHtml(PREMIUM_BOX_CONFIG.closeLabel)}"><span aria-hidden="true">&times;</span></button>
          <div class="pbu-modal__image-wrap"><img class="pbu-modal__image" src="${PREMIUM_BOX_CONFIG.imageUrl}" alt="${escapeHtml(PREMIUM_BOX_CONFIG.title)}" loading="eager" width="600" height="600"></div>
          <div class="pbu-modal__body">
            <h2 id="pbu-buynow-title" class="pbu-modal__title">${escapeHtml(PREMIUM_BOX_CONFIG.buyNowTitle)}</h2>
            <p class="pbu-modal__desc">${escapeHtml(PREMIUM_BOX_CONFIG.buyNowDesc)}</p>
            <p class="pbu-modal__price">${escapeHtml(PREMIUM_BOX_CONFIG.title)} — ${escapeHtml(PREMIUM_BOX_CONFIG.priceText)}</p>
            <div class="pbu-modal__status" role="status" aria-live="polite" hidden></div>
            <div class="pbu-modal__actions">
              <button type="button" class="pbu-modal__select" data-pbu-select>${escapeHtml(PREMIUM_BOX_CONFIG.buyNowSelectLabel)}</button>
              <button type="button" class="pbu-modal__decline" data-pbu-decline>${escapeHtml(PREMIUM_BOX_CONFIG.buyNowDeclineLabel)}</button>
            </div>
          </div>
        </div>`;
      document.body.appendChild(dialogEl);

      dialogEl.addEventListener('click', (e) => {
        if (e.target.closest('[data-pbu-close]')) {
          cancelTimer();
          if (handlers && handlers.onDecline) handlers.onDecline();
          return;
        }
        if (e.target.closest('[data-pbu-decline]')) {
          cancelTimer();
          if (handlers && handlers.onDecline) handlers.onDecline();
          return;
        }
        if (e.target.closest('[data-pbu-select]')) {
          cancelTimer();
          if (handlers && handlers.onSelect) handlers.onSelect();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (dialogEl.hidden) return;
        if (e.key === 'Escape') {
          cancelTimer();
          if (handlers && handlers.onDecline) handlers.onDecline();
        }
        if (e.key === 'Tab') trapFocus(e);
      });

      return dialogEl;
    }

    function trapFocus(e) {
      const focusable = dialogEl.querySelectorAll('button, [href], input, [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function cancelTimer() {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
    }

    function resetButtons() {
      const el = dialogEl;
      if (!el) return;
      const select = el.querySelector('[data-pbu-select]');
      const decline = el.querySelector('[data-pbu-decline]');
      select.disabled = false;
      select.textContent = PREMIUM_BOX_CONFIG.buyNowSelectLabel;
      decline.disabled = false;
      decline.textContent = PREMIUM_BOX_CONFIG.buyNowDeclineLabel;
      const status = el.querySelector('.pbu-modal__status');
      status.hidden = true;
      status.classList.remove('is-error');
      status.textContent = '';
    }

    function open(h) {
      handlers = h || {};
      const el = build();
      resetButtons();
      lastFocused = document.activeElement;
      el.hidden = false;
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        const selectBtn = el.querySelector('[data-pbu-select]');
        if (selectBtn) selectBtn.focus();
      });

      // Auto-close defaults to "continue without" — never a silent
      // no-op, since the shield is otherwise sitting on top of a
      // button the customer already tried to use.
      closeTimer = setTimeout(() => {
        if (handlers && handlers.onDecline) handlers.onDecline();
      }, PREMIUM_BOX_CONFIG.autoCloseDelay);

      const panel = el.querySelector('.pbu-modal__panel');
      const cancelOnFirstInteract = () => {
        cancelTimer();
        panel.removeEventListener('pointerdown', cancelOnFirstInteract);
        panel.removeEventListener('keydown', cancelOnFirstInteract);
      };
      panel.addEventListener('pointerdown', cancelOnFirstInteract, { once: true });
      panel.addEventListener('keydown', cancelOnFirstInteract, { once: true });
    }

    function close() {
      if (!dialogEl) return;
      cancelTimer();
      dialogEl.hidden = true;
      document.body.style.overflow = '';
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
      handlers = null;
    }

    function setBusy(label) {
      if (!dialogEl) return;
      dialogEl.querySelector('[data-pbu-select]').disabled = true;
      dialogEl.querySelector('[data-pbu-decline]').disabled = true;
      const status = dialogEl.querySelector('.pbu-modal__status');
      status.hidden = false;
      status.classList.remove('is-error');
      status.textContent = label;
    }

    function showError(message) {
      if (!dialogEl) return;
      resetButtons();
      const status = dialogEl.querySelector('.pbu-modal__status');
      status.hidden = false;
      status.classList.add('is-error');
      status.textContent = message;
    }

    return { open, close, setBusy, showError };
  })();

  /* ================================================================
     13. BUY IT NOW — SHIELD OVERLAY + INTERCEPT LOGIC
         Never touches, moves, or reparents Shopify's own button,
         its shadow DOM, or its iframe. Positions a same-origin
         sibling on top, measured with getBoundingClientRect and
         kept in sync with ResizeObserver.
     ================================================================ */
  const shieldedContainers = new WeakSet();
  let buyNowBusy = false;

  function scanForBuyNowContainers() {
    const found = [];
    const forms = document.querySelectorAll(PRODUCT_FORM_SELECTORS.join(','));
    forms.forEach((form) => {
      const scope =
        (form.closest && form.closest('product-form-component, .shopify-section, [id^="MainProduct"], .product')) ||
        form.parentElement ||
        document;
      const container = firstMatch(BUY_NOW_CONTAINER_SELECTORS, scope);
      if (container) found.push({ container, form });
    });
    return found;
  }

  function shieldBuyNowButtons() {
    const candidates = scanForBuyNowContainers();
    candidates.forEach(({ container, form }) => {
      if (shieldedContainers.has(container)) return;
      // Fail-safe: if we can't determine the selected variant, don't
      // shield at all — leave the native button completely untouched
      // rather than covering it with something we can't drive.
      if (!getSelectedVariantId(form)) return;
      shieldContainer(container, form);
    });
  }

  function shieldContainer(container, form) {
    const parent = container.parentElement;
    if (!parent || !document.body.contains(container)) return;

    if (getComputedStyle(parent).position === 'static') {
      parent.style.position = 'relative';
    }

    const overlay = document.createElement('div');
    overlay.className = 'pbu-buynow-shield';
    overlay.setAttribute('role', 'button');
    overlay.setAttribute('tabindex', '0');
    overlay.setAttribute('aria-label', PREMIUM_BOX_CONFIG.offerTitle + ' — ' + PREMIUM_BOX_CONFIG.buyNowTitle);
    parent.insertBefore(overlay, container.nextSibling);
    shieldedContainers.add(container);

    function sync() {
      if (!document.body.contains(container) || !document.body.contains(overlay)) {
        cleanup();
        return;
      }
      const cRect = container.getBoundingClientRect();
      const pRect = parent.getBoundingClientRect();
      overlay.style.position = 'absolute';
      overlay.style.top = cRect.top - pRect.top + 'px';
      overlay.style.left = cRect.left - pRect.left + 'px';
      overlay.style.width = cRect.width + 'px';
      overlay.style.height = cRect.height + 'px';
    }

    let ro = null;
    try {
      ro = new ResizeObserver(sync);
      ro.observe(container);
      ro.observe(parent);
    } catch (e) {
      /* ResizeObserver unsupported — sync() still runs on the events below */
    }

    function onWindowChange() {
      sync();
    }
    window.addEventListener('resize', onWindowChange);
    window.addEventListener('scroll', onWindowChange, true);

    function cleanup() {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', onWindowChange);
      window.removeEventListener('scroll', onWindowChange, true);
      if (overlay.parentElement) overlay.parentElement.removeChild(overlay);
    }

    sync();

    overlay.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleBuyNowIntercept(container, form, overlay);
    });
    overlay.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleBuyNowIntercept(container, form, overlay);
      }
    });

    log('Shielded a Buy It Now container.', container);
  }

  function handleBuyNowIntercept(container, form, overlay) {
    if (buyNowBusy) return;
    const variantId = getSelectedVariantId(form);
    const quantity = getSelectedQuantity(form);
    if (!variantId) return; // fail-safe: nothing we can safely do

    dispatchAnalytics('buy_now_modal_opened', { variantId, quantity });

    BuyNowModal.open({
      onSelect: () => runBuyNowFlow({ variantId, quantity, withPremiumBox: true, overlay }),
      onDecline: () => runBuyNowFlow({ variantId, quantity, withPremiumBox: false, overlay }),
    });
  }

  async function runBuyNowFlow({ variantId, quantity, withPremiumBox, overlay }) {
    if (buyNowBusy) return;
    buyNowBusy = true;
    if (overlay) overlay.setAttribute('data-pbu-busy', '1');
    BuyNowModal.setBusy(
      withPremiumBox ? PREMIUM_BOX_CONFIG.buyNowProcessingLabel : PREMIUM_BOX_CONFIG.buyNowContinuingLabel
    );

    try {
      const items = [{ id: variantId, quantity }];

      if (withPremiumBox) {
        // Authoritative re-check right before writing — never add a
        // second Premium Box if one is already in the cart.
        const currentCart = await fetchCart().catch(() => null);
        const alreadyHasBox = currentCart && findPremiumBoxLine(currentCart);
        if (!alreadyHasBox) {
          items.push({ id: PREMIUM_BOX_CONFIG.variantId, quantity: 1 });
        }
      }

      const { cartAddUrl, checkoutUrl } = getRoutes();
      const res = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.description || body.message || 'Could not update cart.');
      }

      const finalCart = await fetchCart().catch(() => null);
      await notifyThemeOfCartChange(finalCart);
      dispatchAnalytics('buy_now_continue', { withPremiumBox, variantId, quantity });

      // Real Shopify checkout, carrying the real cart — never a fake
      // total, never checkout-page DOM manipulation.
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error('[PremiumBoxUpsell] Buy It Now flow failed:', err);
      buyNowBusy = false;
      if (overlay) overlay.removeAttribute('data-pbu-busy');
      BuyNowModal.showError(PREMIUM_BOX_CONFIG.buyNowErrorLabel);
      dispatchAnalytics('buy_now_failed', { message: err && err.message, withPremiumBox });
    }
  }

  /* ================================================================
     14. INIT — safe regardless of when/where the script loads
     ================================================================ */
  function init() {
    mountAll();
    wireLiveUpdates();
    syncAllBanners();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();